import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Session, Therapist } from '../../../core/types';
import { AppBar, LoadingWidget, Dialog, FadeInAnimation } from '../../../core/components/ui';
import { ENV } from '../../../core/api/client';

declare global {
  interface Window {
    JitsiMeetExternalAPI?: any;
  }
}

const ConferencePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = (location.state as any)?.session as Session | undefined;
  const therapist = (location.state as any)?.therapist as Therapist | undefined;

  const jitsiContainerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<any>(null);

  const [loading, setLoading] = useState(true);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [jitsiLoaded, setJitsiLoaded] = useState(false);

  const jitsiUrl = ENV.JITSI_URL || 'https://meet.jit.si';
  const roomName = session?.meetingLink
    ? session.meetingLink.split('/').pop() || `therapp-${session.id}`
    : `therapp-${session?.id || Date.now()}`;

  // useEffect(() => {
  //   if (!session) { navigate(-1); return; }
  //   loadJitsi();
  //   return () => { apiRef.current?.dispose?.(); };
  // }, []);
useEffect(() => {
  if (!session) { navigate(-1); return; }

  let cancelled = false;
  loadJitsi(() => cancelled);

  return () => {
    cancelled = true;
    apiRef.current?.dispose?.();
    apiRef.current = null;
  };
}, []);

const loadJitsi = (isCancelled: () => boolean) => {
  const domain = jitsiUrl.replace(/^https?:\/\//, '');

  if (window.JitsiMeetExternalAPI) {
    if (!isCancelled()) initJitsi(domain);
    return;
  }

  // Reuse an in-flight script tag instead of adding a second one
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="https://${domain}/external_api.js"]`
  );
  if (existing) {
    existing.addEventListener('load', () => {
      if (!isCancelled()) { setJitsiLoaded(true); initJitsi(domain); }
    });
    return;
  }

  const script = document.createElement('script');
  script.src = `https://${domain}/external_api.js`;
  script.onload = () => {
    if (!isCancelled()) { setJitsiLoaded(true); initJitsi(domain); }
  };
  script.onerror = () => setLoading(false);
  document.head.appendChild(script);
};
  // const loadJitsi = () => {
  //   const domain = jitsiUrl.replace(/^https?:\/\//, '');
  //   if (window.JitsiMeetExternalAPI) {
  //     initJitsi(domain);
  //     return;
  //   }
  //   const script = document.createElement('script');
  //   script.src = `https://${domain}/external_api.js`;
  //   script.onload = () => { setJitsiLoaded(true); initJitsi(domain); };
  //   script.onerror = () => setLoading(false);
  //   document.head.appendChild(script);
  // };

  const initJitsi = (domain: string) => {
    if (!jitsiContainerRef.current || !window.JitsiMeetExternalAPI) return;
    try {
      apiRef.current = new window.JitsiMeetExternalAPI(domain, {
        roomName,
        width: '100%',
        height: '100%',
        parentNode: jitsiContainerRef.current,
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinConfig: { enabled: false }, // newer Jitsi versions use this
        },
        interfaceConfigOverwrite: {
          TOOLBAR_BUTTONS: ['microphone', 'camera', 'hangup', 'chat', 'tileview'],
          SHOW_JITSI_WATERMARK: false,
          SHOW_BRAND_WATERMARK: false,
        },
        userInfo: {
          displayName: 'Patient',
        },
      });
      // apiRef.current.addEventListener('readyToClose', () => setEndDialogOpen(true));
      // apiRef.current.addEventListener('videoConferenceJoined', () => setLoading(false));
      apiRef.current.addEventListener('videoConferenceJoined', () => setLoading(false));
apiRef.current.addEventListener('readyToClose', () => setEndDialogOpen(true));
apiRef.current.addEventListener('participantRoleChanged', () => setLoading(false)); // fallback
    } catch {
      setLoading(false);
    }
  };

  const handleEnd = () => {
    apiRef.current?.executeCommand?.('hangup');
    navigate(-1);
  };

  if (!session) return null;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#111' }}>
      {loading && <LoadingWidget />}

      {/* Minimal appbar */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', background: 'rgba(0,0,0,0.7)', zIndex: 10, flexShrink: 0,
      }}>
        <div style={{ color: 'white', fontWeight: 600, fontSize: 15 }}>
          {therapist?.name || 'Session'}
        </div>
        <button
          onClick={() => setEndDialogOpen(true)}
          style={{
            padding: '6px 14px', borderRadius: 99,
            background: 'var(--color-error)', color: 'white',
            fontWeight: 600, fontSize: 13,
          }}
        >
          End
        </button>
      </div>

      {/* Jitsi container */}
      <div
        ref={jitsiContainerRef}
        style={{ flex: 1, width: '100%' }}
      />

      {/* Fallback link */}
      {!jitsiLoaded && !loading && (
        <FadeInAnimation delay={0}>
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 16, padding: 24, color: 'white',
          }}>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
              Could not load the video call. Please use the link below:
            </p>
            <a
              href={session.meetingLink || `${jitsiUrl}/${roomName}`}
              target="_blank"
              rel="noreferrer"
              style={{
                padding: '12px 24px', background: 'var(--color-primary)',
                borderRadius: 10, color: 'white', fontWeight: 600,
              }}
            >
              Open Meeting Link
            </a>
          </div>
        </FadeInAnimation>
      )}

      {/* End Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>End Session?</h3>
        <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginBottom: 20 }}>
          Are you sure you want to leave this session?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setEndDialogOpen(false)} style={{ flex: 1, height: 44, borderRadius: 8, background: 'var(--color-surface-variant)', color: 'var(--color-on-surface-variant)', fontWeight: 600 }}>
            Stay
          </button>
          <button onClick={handleEnd} style={{ flex: 1, height: 44, borderRadius: 8, background: 'var(--color-error)', color: 'white', fontWeight: 700 }}>
            Leave
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default ConferencePage;
